import querystring from 'querystring'

import get from 'lodash.get'
import config from 'config'

import { extractNumber } from '@cred/shared/utils'
import { getWidgetAppIframeId } from '@cred/widget-sdk-client'
import { logger } from '@cred/fe-logger'
import { currency } from '@cred/shared/formatters'

const creditScoreRange = {
  850: '750+',
  749: '700-749',
  699: '640-699',
  639: '<640',
}

const formatRole = (role) => {
  switch (role) {
    case 'borrower':
      return 'Student'
    case 'cosigner':
      return 'Co-signer'
    default:
      return role
  }
}

const generateFilterText = (filterValues, vertical) => {
  switch (vertical) {
    case 'inschool':
      return `I am a ${formatRole(filterValues.role)} looking for a ${currency(
        filterValues.loanAmount,
      )} student loan`
    case 'high-yield-savings':
      return `${currency(filterValues.balanceAmount)} Balance amount`
    default:
      return `${currency(filterValues.loanAmount)} loan amount and ${
        creditScoreRange[filterValues.creditScoreApprox]
      } credit score`
  }
}

const getCreditScore = (value) => parseInt(value, 10)

const getLoanAmount = (value) => extractNumber(value)
const hasPagination = (variation) => variation !== 'full'

const getPageStats = (variation, lenders, page, LENDER_PER_PAGE) => {
  const withPagination = hasPagination(variation)
  const pageCount = Math.ceil(lenders.length / LENDER_PER_PAGE)
  const pageFrom = page * LENDER_PER_PAGE
  const pageTo = withPagination ? page * LENDER_PER_PAGE + LENDER_PER_PAGE : lenders.length
  const showPagination = withPagination && pageCount > 1

  return { pageCount, pageFrom, pageTo, showPagination }
}

const getCtaPath = (vertical) => {
  switch (vertical) {
    case 'inschool':
      return 'private-student-loans/get-started'
    default:
      return ''
  }
}

const getCtaUrl = (vertical, theme, utms) => {
  const params = {
    utm_source: utms.utm_source,
    utm_medium: utms.utm_medium,
    utm_campaign: utms.utm_campaign,
    utm_content: utms.utm_content,
    credclid: utms.credclid,
    renderId: utms.renderId,
  }
  const path = getCtaPath(vertical, theme)

  return `${config.credibleRoot}/${path}?${querystring.encode(params)}`
}

// This convenience method uses lodash's get and tries to key into
// widgetContext using array keys. widgetContext's structure is
// defined in WidgetApp.js prop types. If any errors occur, defaults to last
// argument ({})
const getPartnerDefaultFilterValues = (widgetContext, vertical) =>
  get(widgetContext, ['options', 'multiVertical', 'defaultFilters', vertical], {})

const getMMMDDFormat = (dateArr, months) =>
  `${months[parseInt(dateArr[1], 10) - 1].slice(0, 3)} ${parseInt(dateArr[2], 10)}, ${parseInt(
    dateArr[0],
    10,
  )}`

const scrollToIframeTop = () => {
  try {
    const widgetId = getWidgetAppIframeId(logger)
    const root = window?.parent?.document?.getElementById?.(widgetId)
    if (!root) {
      logger.error?.('Getting iframe element from parent')
      return undefined
    }

    const isVisibleInViewport = (element) => {
      const rect = element?.getBoundingClientRect?.()
      return rect?.top >= 0
    }

    if (!isVisibleInViewport(root)) {
      root.scrollIntoView({ behavior: 'smooth' })
    }
    return undefined
  } catch (e) {
    logger.error?.('Scrolling to iframe top', e)
    return undefined
  }
}

export {
  formatRole,
  generateFilterText,
  getCreditScore,
  getLoanAmount,
  hasPagination,
  getMMMDDFormat,
  getPageStats,
  getCtaPath,
  getCtaUrl,
  getPartnerDefaultFilterValues,
  scrollToIframeTop,
}
